import {
  DeleteOutlined as CancelIcon,
  EditOutlined as EditIcon,
  ClockCircleOutlined as LastMinuteIcon,
  ZoomInOutlined as ViewIcon,
} from '@ant-design/icons'
import { Button, Card, Typography } from 'antd'
import { memo, useContext, useState } from 'react'
import {
  bookingOwnerIsAlsoParticipant,
  getBookingScheduleInfo,
  getBookingTag,
  getPlayerFirstNameAndLastInitial,
  getPlayersText,
  isBallMachineAvailable,
  mayBeCancelled,
  mayBeEdited,
  populateWithParent,
  userIsBookingOwner,
} from '../../util/bookingUtil'

import _ from 'lodash'
import useResponsive from '../../hooks/useResponsive'
import { formatDateTime } from '../../util/dateTimeUtil'
import AppContext from '../common/AppContext'

const { Paragraph } = Typography

const calculateRow = (dateTimeString) => {
  try {
    const serverTime = formatDateTime(dateTimeString, 'HH:mm')
    const { START_HOUR, SLOT_DURATION } = getBookingScheduleInfo(new Date())
    const [hours, minutes] = serverTime.split(':').map(Number)
    const totalMinutesFromStart = (hours - START_HOUR) * 60 + minutes

    // +1 because CSS Grid is 1-indexed,
    // and another +1 if you have a header row
    return Math.floor(totalMinutesFromStart / SLOT_DURATION) + 1
  } catch (e) {
    console.error(e)
  }
}

export const calculateCourtNumbers = (courts) => {
  const result = []

  for (const court of courts) {
    const match = court.label.match(/(\d+)$/)
    result.push(match ? parseInt(match, 10) : 0)
  }

  return result
}

// Function to get column span
const getColumnStyles = (courts) => {
  const courtNumbers = calculateCourtNumbers(courts)
  const minCourt = Math.min(...courtNumbers)
  const maxCourt = Math.max(...courtNumbers)

  return {
    gridColumnStart: minCourt + 1, // +1 for the time label column
    gridColumnEnd: maxCourt + 2, // +2 to cover the full width of the last court
  }
}

export const getTagColour = (bookingType) => {
  switch (bookingType) {
    case 'Lesson':
      return 'cyan'
    case 'Ladder':
      return 'blue'
    case 'Tournament':
      return 'purple'
    default:
      return 'geekblue'
  }
}

const Booking = memo(({ booking, onAdd, onEdit, onDelete, isDragging = false }) => {
  // console.log('Rendering booking', booking?.id)
  booking = populateWithParent(booking)
  const bp = useResponsive()
  const [hovered, setHovered] = useState(false)
  const { currentUser } = useContext(AppContext)
  const colStyles = getColumnStyles(booking.courts)
  const cancelAllowed = mayBeCancelled(booking, currentUser)
  const editAllowed = mayBeEdited(booking, currentUser)
  const viewAllowed = true
  const isBookedByCurrentUser = currentUser ? currentUser.id === booking.createdByUser.id : false
  const isCurrentUserParticipant =
    currentUser &&
    _.includes([booking.player1?.id, booking.player2?.id, booking.player3?.id, booking.player4?.id], currentUser.id)

  let status
  if (isBookedByCurrentUser) {
    status = 'bookedByCurrentUser'
  } else if (isCurrentUserParticipant) {
    status = 'currentUserIsParticipant'
  } else {
    status = 'booked'
  }

  const ballMachineStyle = isBallMachineAvailable(booking.startTime) ? 'ballMachine' : ''

  let isBlocked = booking?.bookingType === 'Blocked'
  let isTournamentMatch = booking?.bookingType === 'Tournament'

  const desc = booking?.description ? (
    <Paragraph className={`bookingDescription`}>{booking.description}</Paragraph>
  ) : null
  const result = booking?.result ? booking?.result + ' ' : null

  const commentClassName = isTournamentMatch ? 'tournamentBookingComment' : 'bookingComment'
  const message = null
  let comment
  if (isBlocked) {
    comment =
      booking?.comment || message || result ? (
        <Paragraph className={`bookingBlockedMessage`}>{booking?.comment || message}</Paragraph>
      ) : null
  } else {
    if (result) {
      comment = (
        <Paragraph className={commentClassName}>
          {booking?.comment || message ? (booking?.comment || message) + ': ' : null}
          <span className="bookingMatchResult">{result}</span>
        </Paragraph>
      )
    } else {
      comment =
        booking?.comment || message ? (
          <Paragraph className={commentClassName}>
            <span className="bookingMatchResult">{result}</span>
            {booking?.comment || message}
          </Paragraph>
        ) : null
    }
  }

  let ownerName = null
  if (booking?.createdByUser) {
    ownerName = getPlayerFirstNameAndLastInitial(booking.createdByUser, true)
  }

  // Include the owner in the player list only if its a tournament match and the owner is a player too.
  const includeOwnerInPlayerList = isTournamentMatch && bookingOwnerIsAlsoParticipant(booking)
  const playersStr = getPlayersText(booking, includeOwnerInPlayerList)

  let showOwner = isTournamentMatch || isBlocked ? false : booking?.showOwner
  let bookingPlayersClassname = isTournamentMatch ? 'bookingPlayersNoPad' : 'bookingPlayers'

  const owner = ownerName && showOwner ? <span className="bookingOwner">{ownerName}</span> : null

  let tags = []
  if (booking && booking.bookingType !== 'General' && booking.bookingType !== 'Blocked') {
    tags.push(getBookingTag(booking, bp))
  }

  const handleDelete = () => {
    if (booking && onDelete) {
      onDelete(booking)
    }
  }
  const handleEdit = () => {
    if (booking && onEdit) {
      onEdit(booking, !userIsBookingOwner(currentUser, booking))
    }
  }

  const actions = (
    <div className="bookingActions">
      {editAllowed && (
        <Button type="link" onClick={handleEdit} icon={<EditIcon className="bookingActionIcon" />} title="Edit" />
      )}
      {viewAllowed && !editAllowed && (
        <Button
          type="link"
          onClick={handleEdit}
          icon={<ViewIcon className="bookingActionIcon" />}
          title="View"
        ></Button>
      )}
      {cancelAllowed && (
        <Button type="link" onClick={handleDelete} icon={<CancelIcon className="bookingActionIcon" />} title="Delete" />
      )}
    </div>
  )

  const lastMinuteIndicator = (
    <span className="bookingLastMinute" title="This was a last-minute booking">
      <LastMinuteIcon twoToneColor={'blue'} />
    </span>
  )

  if (booking.isLastMinute && !isTournamentMatch && !isBlocked) {
    tags = [...tags, lastMinuteIndicator]
  }

  const gridRowStart = calculateRow(booking.startTime) + 1 // +1 for header
  const gridRowEnd = calculateRow(booking.endTime) + 1

  return (
    <Card
      key={booking.id}
      size="small"
      title={booking.name}
      bordered={false}
      className={`bookingTimeslot ${status} ${ballMachineStyle} ${booking.bookingType}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...colStyles,
        gridRowStart,
        gridRowEnd,
        zIndex: hovered ? 2 : 2,
        pointerEvents: isDragging ? 'none' : 'auto',
        overflow: 'visible',
        cursor: currentUser ? 'pointer' : 'default',
      }}
      onClick={currentUser ? handleEdit : () => {}}
      bodyStyle={{
        height: '100%',
        paddingBottom: 0,
      }}
    >
      <div className="bookingContent">
        {desc}
        {owner}
        {isTournamentMatch && comment}
        {playersStr && <div className={bookingPlayersClassname}>{playersStr}</div>}
        {!isTournamentMatch && comment}
        {!_.isEmpty(tags) && <div className="bookingTag">{tags}</div>}
      </div>
      {/* {viewAllowed || editAllowed || cancelAllowed ? actions : null} */}
    </Card>
  )
})

export default Booking
